package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileKind
import com.advaita.cloud.drive.ui.api.FilesApi
import com.advaita.cloud.drive.ui.api.userFacingMessage
import com.advaita.cloud.drive.ui.store.CuratedInputFormat
import com.advaita.cloud.drive.ui.store.CuratedInputSpec
import com.advaita.cloud.drive.ui.store.CuratedKnobSpec
import com.advaita.cloud.drive.ui.store.CuratedPick
import com.advaita.cloud.drive.ui.store.CuratedWorkflowSpec
import com.advaita.cloud.drive.ui.store.PickerSource
import com.advaita.cloud.drive.ui.store.PreviewTooltipStore
import com.advaita.cloud.drive.ui.store.SHARED_RUN_OUTPUT_NOTE
import com.advaita.cloud.drive.ui.store.TooltipAnchor
import com.advaita.cloud.drive.ui.store.autoSelectAll
import com.advaita.cloud.drive.ui.store.describeFileTypeGuidance
import com.advaita.cloud.drive.ui.store.formatPickedSize
import com.advaita.cloud.drive.ui.store.matchesFileTypeOrUnconstrained
import com.advaita.cloud.drive.ui.store.mergePicks
import com.advaita.cloud.drive.ui.store.mismatchCaption
import com.advaita.cloud.drive.ui.store.removePick
import com.advaita.cloud.drive.ui.store.scatteredPickRel
import com.advaita.cloud.drive.ui.store.sharedPickerTrail
import com.advaita.cloud.drive.ui.store.totalPickedBytes
import com.advaita.cloud.drive.ui.store.walkFolder
import com.advaita.cloud.drive.ui.store.walkSharedFolder
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * ADV-669 session E — the folder/file picker body for [ModalRequest.CuratedRun].
 *
 * Reuses [Breadcrumb] and [com.advaita.cloud.drive.ui.api.FilesApi.listFiles] (the
 * SAME browse call [FileTable]/[FileBrowserStore] use to render the main file list) —
 * no new browsing mechanism, per D21: the picker enumerates a folder by walking it
 * through that call, under the scientist's own session, rather than sending a folder
 * id to a resolve endpoint and expecting the server to walk it. Everything below
 * mutates only this modal's local [CuratedRunUiState] — nothing is submitted until
 * the outer Modal's "Run" completes with the accumulated picks.
 *
 * ADV-1330: opened in a shared folder ([PickerSource.Shared]), the same views browse by
 * folder id and walk by the recursive listing by id, from the shared root down.
 */

/**
 * Local, per-open-modal state — mirrors [PullFromUrlUiState]'s discipline of living
 * only in this composable's remember scope. Nothing here persists past cancel/submit.
 *
 * [folderFiles] is the folder the modal was opened from — the SAME listing already
 * backing the main file table ([com.advaita.cloud.drive.ui.store.FileBrowserStore.files]
 * at open time), not a fresh fetch. On construction it's run through [autoSelectAll] to
 * pre-select obvious candidates (reviewer feedback: don't open on "No files selected"
 * when the folder plainly has the right files) — synchronously, so there's no loading
 * flicker and no race with the folder changing underneath the modal.
 *
 * [seed] is the wizard's already-made picks, when the scientist is arriving back on step
 * 2 having gone back to the OPC card list. A non-empty seed **replaces** auto-selection:
 * re-guessing over choices the scientist already made (including files they deliberately
 * removed) would silently undo their edits. An all-empty seed — the normal first visit,
 * and what `picksForOpcSwitch` leaves behind after a genuine OPC switch — auto-selects as
 * before.
 */
internal class CuratedRunUiState(
    specs: List<CuratedInputSpec>,
    /** ADV-1330: My Drive's folder, or a shared trail. It decides how [CuratedBrowseView] lists. */
    val source: PickerSource,
    folderFiles: List<FileDto>,
    seed: Map<String, List<CuratedPick>> = emptyMap(),
    knobSpecs: List<CuratedKnobSpec> = emptyList(),
) {
    /** Picks accumulated so far, keyed by [CuratedInputSpec.name]. */
    val picksByInput = mutableStateMapOf<String, List<CuratedPick>>().apply {
        specs.forEach { put(it.name, seed[it.name].orEmpty()) }
    }

    /**
     * Scalar knob values, keyed by [CuratedKnobSpec.name], seeded from each knob's
     * declared default.
     *
     * Seeding here is the whole reason a curator's `default` means anything: nothing
     * downstream applies one (wisp's `hydrate` takes the submitted knobs verbatim), so an
     * unrendered default is not a fallback, it is an absence. Starting the control at the
     * declared value means the run carries a value the scientist actually saw.
     */
    val knobValues = mutableStateMapOf<String, String>().apply {
        knobSpecs.forEach { put(it.name, it.initial) }
    }

    /** Set once, at construction, when the initial auto-selection (below) chose at
     *  least one file — the on-screen "N files auto-selected from <folder>" note. A
     *  scientist who doesn't realise files were chosen for them is the real risk here,
     *  so this is never left silent. Null when nothing was auto-selected — the overview
     *  then looks exactly as it did before this feature (plain "No files selected"). */
    var autoSelectNote: String? by mutableStateOf(null)

    init {
        if (seed.values.all { it.isEmpty() }) {
            val selection = autoSelectAll(specs, folderFiles)
            selection.picksByInput.forEach { (name, picks) -> picksByInput[name] = picks }
            if (selection.totalSelected > 0) {
                val plural = if (selection.totalSelected == 1) "" else "s"
                autoSelectNote = "${selection.totalSelected} file$plural auto-selected from " +
                    "${source.folderName} — review before running."
            }
        }
    }

    /** null = the overview (every input + its current picks); non-null = browsing
     *  Drive to add picks for that one input. */
    var browsingInput: String? by mutableStateOf(null)
    var browsePath: String by mutableStateOf("/")
    /** ADV-1330: in a [PickerSource.Shared] picker, the folder being browsed, from the shared root. */
    var browseTrail: List<FileDto> by mutableStateOf((source as? PickerSource.Shared)?.trail.orEmpty())
    var browseChildren: List<FileDto> by mutableStateOf(emptyList())
    var browseLoading: Boolean by mutableStateOf(false)
    var browseError: String? by mutableStateOf(null)

    /** file-set multi-select: fileId -> the row it was checked from. A map (not a
     *  Set<id>) because leaving the directory the checkbox was ticked in must not
     *  lose that file's name/size — this is what makes "files across several
     *  directories" (D4's second shape) accumulate correctly. */
    val checkedFiles = mutableStateMapOf<String, FileDto>()

    /** Set once a folder walk finishes, before its picks are merged in — the
     *  "count + total size, deselect before adding" review the spec requires. */
    var scanReview: FolderScanReview? by mutableStateOf(null)
    var scanning: Boolean by mutableStateOf(false)

    fun enterBrowsing(inputName: String) {
        browsingInput = inputName
        when (source) {
            is PickerSource.Own -> browsePath = source.path
            is PickerSource.Shared -> browseTrail = source.trail
        }
        browseChildren = emptyList()
        browseError = null
        checkedFiles.clear()
        scanReview = null
    }

    fun exitBrowsing() {
        browsingInput = null
        checkedFiles.clear()
        scanReview = null
    }
}

/** One folder walk's result, awaiting the scientist's review before it's merged into
 *  [CuratedRunUiState.picksByInput]. [picks] already excludes files that didn't satisfy
 *  the input's declared file-type constraint (see [walkFolder]); [totalFiles] is how
 *  many leaf files the walk visited in total, so `totalFiles - picks.size` is the count
 *  the scan silently would have dropped if this arithmetic weren't shown (reviewer
 *  feedback — see [FolderScanReviewView]'s "N of M files matched" line). [deselected]
 *  starts empty — every MATCHED file is included by default, and unchecking one removes
 *  it from what gets added. */
internal class FolderScanReview(val folderPath: String, val picks: List<CuratedPick>, val totalFiles: Int) {
    val deselected = mutableStateListOf<String>()
}

/**
 * Step 1 of the OPC wizard — one card per opinionated use-case, carrying the curator's
 * title and their one-line summary.
 *
 * No provenance line (pipeline + pinned revision). It was here on the argument that a
 * bioinformatician needs both for trust and citation, and that may well come back — but
 * wisp's `GET /offerings`, the catalog projection we are moving to, serves
 * `{job_type, title, description, size, parameters, file_inputs}` and carries neither.
 * Rendering them meant hardcoding a second copy of the registry on this side, which is
 * exactly the duplication that let a card claim `1.0.2` while the registered document
 * pinned `1.0.1`. Better absent than confidently wrong. Ask wisp to project them if a
 * scientist asks for them.
 *
 * Rejected shapes, so nobody re-proposes them: a split-button dropdown (no room for the
 * curator's prose, which is the whole point of an *opinionated* use-case) and a full
 * Workflows gallery route (the right answer once the catalog is real — and this card list
 * is exactly the component that gallery will reuse, so this is not a detour).
 *
 * `data-opc` is a stable hook for the Playwright spec; matching on prose would break the
 * moment a curator reworded a title.
 */
@Composable
internal fun OpcSelectView(wizard: OpcWizardUiState, catalog: List<CuratedWorkflowSpec>) {
    Div(attrs = { classes("opc-cards") }) {
        catalog.forEach { spec ->
            val selected = wizard.chosen?.jobType == spec.jobType
            Div(attrs = {
                if (selected) classes("opc-card", "selected") else classes("opc-card")
                attr("role", "button")
                attr("data-opc", spec.jobType)
                onClick { wizard.choose(spec) }
            }) {
                Div(attrs = { classes("opc-card-title") }) { Text(spec.title) }
                P(attrs = { classes("opc-card-summary") }) { Text(spec.description) }
            }
        }
    }
}

@Composable
internal fun CuratedRunBody(
    state: CuratedRunUiState,
    specs: List<CuratedInputSpec>,
    source: PickerSource,
    knobs: List<CuratedKnobSpec> = emptyList(),
    peek: PreviewTooltipStore,
) {
    val browsingInput = state.browsingInput
    if (browsingInput == null) {
        CuratedOverview(state, specs, source, knobs)
    } else {
        val spec = specs.firstOrNull { it.name == browsingInput } ?: return
        CuratedBrowseView(state, spec, peek)
    }
}

/** The list of declared inputs, each with its description, current picks (with
 *  per-file deselect), and an action to start/edit its selection. */
@Composable
private fun CuratedOverview(
    state: CuratedRunUiState,
    specs: List<CuratedInputSpec>,
    source: PickerSource,
    knobs: List<CuratedKnobSpec> = emptyList(),
) {
    state.autoSelectNote?.let { note ->
        Div(attrs = { classes("curated-auto-select-note") }) { Text(note) }
    }
    // ADV-1330 (scope 9): on shared files the outputs land in the anchor's folder, which is the
    // owner's, and not in the person's own drive. The wizard says so before Run.
    if (source is PickerSource.Shared) {
        Div(attrs = { classes("curated-output-note") }) { Text(SHARED_RUN_OUTPUT_NOTE) }
    }
    Div(attrs = { classes("curated-inputs") }) {
        specs.forEach { spec ->
            val picks = state.picksByInput[spec.name].orEmpty()
            Div(attrs = { classes("curated-input") }) {
                Div(attrs = { classes("curated-input-header") }) {
                    Span(attrs = { classes("curated-input-name") }) { Text(spec.name) }
                    if (!spec.required) {
                        Span(attrs = { classes("curated-input-optional") }) { Text("optional") }
                    }
                }
                P(attrs = { classes("curated-input-desc") }) { Text(spec.description) }
                if (picks.isEmpty()) {
                    Div(attrs = { classes("curated-input-empty") }) { Text("No files selected") }
                } else {
                    Div(attrs = { classes("curated-input-summary") }) {
                        Text("${picks.size} file${if (picks.size == 1) "" else "s"} · ${formatPickedSize(totalPickedBytes(picks))}")
                    }
                    Div(attrs = { classes("curated-pick-list") }) {
                        picks.forEach { pick ->
                            Div(attrs = { classes("curated-pick-row") }) {
                                Span(attrs = { classes("curated-pick-rel") }) { Text(pick.rel) }
                                Span(attrs = { classes("curated-pick-size") }) {
                                    Text(formatPickedSize(pick.sizeBytes ?: 0L))
                                }
                                Button(attrs = {
                                    classes("btn", "icon")
                                    attr("data-tip", "Remove ${pick.displayName}")
                                    attr("aria-label", "Remove ${pick.displayName}")
                                    onClick { state.picksByInput[spec.name] = removePick(picks, pick.fileId) }
                                }) { Icon(IconName.CLOSE) }
                            }
                        }
                    }
                    // Quiet, non-blocking caution — a curator's pattern can be wrong,
                    // absent, or narrower than reality, and the scientist's pick already
                    // stands; this only informs, it never re-opens the question. The Run
                    // button (formIsComplete) has no dependency on this.
                    mismatchCaption(spec, picks)?.let { caption ->
                        Div(attrs = { classes("curated-mismatch-caution") }) { Text(caption) }
                    }
                }
                Button(attrs = {
                    classes("btn")
                    onClick { state.enterBrowsing(spec.name) }
                }) {
                    Text(
                        when {
                            spec.format == CuratedInputFormat.FILE_PATH && picks.isNotEmpty() -> "Change file"
                            spec.format == CuratedInputFormat.FILE_PATH -> "Choose file"
                            picks.isEmpty() -> "Add files or a folder…"
                            else -> "Add more…"
                        },
                    )
                }
            }
        }
        // The scalar knobs are PEERS of the file inputs, in the same scrolling list and
        // the same card, because they are the same kind of thing: something you set for
        // this run. Rendered in their own section below the list they read as a different
        // class of control that happened to be bolted on — which is exactly how it looked
        // on the rig, and it sat OUTSIDE the list's 55vh scroll box, so it was the one
        // block that never scrolled with the rest.
        //
        // Same classes as an input, not parallel ones: `curated-input` and friends. A
        // second set of near-identical styles is how two things that should look the same
        // drift apart.
        knobs.forEach { knob ->
            val current = state.knobValues[knob.name].orEmpty()
            Div(attrs = { classes("curated-input") }) {
                Div(attrs = { classes("curated-input-header") }) {
                    Span(attrs = { classes("curated-input-name") }) { Text(knob.name) }
                    // Mirrors the "optional" pill's geometry so the header reads as one
                    // pattern; tinted rather than grey because a reference set is a
                    // different KIND of value, not an optional one.
                    if (knob.isReference) {
                        Span(attrs = { classes("curated-input-badge") }) { Text("reference set") }
                    }
                    // Marks the EXCEPTION, exactly as the file inputs above do — "optional"
                    // when it is, nothing when it is not. The first cut badged `required`
                    // instead, which put a red "required" on the one knob while the two
                    // required file inputs above it carried no badge at all: the same list
                    // saying "this one matters" about the only field that was already
                    // defaulted. Every input in these documents is required by default, so
                    // required is the norm and needs no ink; red also reads as an error on a
                    // form where nothing is wrong.
                    if (!knob.required) {
                        Span(attrs = { classes("curated-input-optional") }) { Text("optional") }
                    }
                }
                if (knob.description.isNotBlank()) {
                    P(attrs = { classes("curated-input-desc") }) { Text(knob.description) }
                }
                // The curator's longer prose as a second paragraph, same size and colour:
                // one description broken where they broke it, not a different tier of text.
                knob.helpText?.takeIf { it.isNotBlank() }?.let {
                    P(attrs = { classes("curated-input-desc") }) { Text(it) }
                }
                if (knob.choices.isNotEmpty()) {
                    Select(attrs = {
                        id("knob-${knob.name}")
                        classes("curated-knob-control")
                        onChange { e -> state.knobValues[knob.name] = e.value.orEmpty() }
                    }) {
                        // No blank option for a knob whose default is already a legal
                        // choice — offering "nothing" invites a submit that omits it, and
                        // an omitted reference is a run with no genome.
                        if (!knob.required && knob.initial.isBlank()) {
                            Option(value = "", attrs = { if (current.isBlank()) selected() }) { Text("—") }
                        }
                        knob.choices.forEach { choice ->
                            Option(value = choice, attrs = { if (choice == current) selected() }) { Text(choice) }
                        }
                    }
                } else {
                    Input(type = InputType.Text, attrs = {
                        id("knob-${knob.name}")
                        classes("curated-knob-control")
                        value(current)
                        onInput { state.knobValues[knob.name] = it.value }
                    })
                }
            }
        }
    }
}

/** Browsing Drive to pick files/a folder for one input. */
@Composable
private fun CuratedBrowseView(state: CuratedRunUiState, spec: CuratedInputSpec, peek: PreviewTooltipStore) {
    val shared = state.source is PickerSource.Shared
    LaunchedEffect(state.browsePath, state.browseTrail) {
        state.browseLoading = true
        state.browseError = null
        try {
            // ADV-1330: a shared folder lists by id. `?path=` would read the caller's own drive.
            state.browseChildren = if (shared) FilesApi.listFolderById(state.browseTrail.last().id)
            else FilesApi.listFiles(state.browsePath)
        } catch (e: Throwable) {
            state.browseError = e.userFacingMessage()
        } finally {
            state.browseLoading = false
        }
    }

    val review = state.scanReview
    if (review != null) {
        FolderScanReviewView(state, spec, review)
        return
    }

    Div(attrs = { classes("curated-browse") }) {
        P(attrs = { classes("modal-message") }) { Text(spec.description) }
        if (shared) {
            SharedBreadcrumb(
                trail = state.browseTrail.map { it.displayName },
                onNavigate = { depth -> state.browseTrail = sharedPickerTrail(state.browseTrail, depth) },
                showRoot = false,
            )
        } else {
            Breadcrumb(path = state.browsePath, onNavigate = { p -> state.browsePath = p })
        }
        when {
            state.scanning -> Div(attrs = { classes("status-pad") }) { Text("Scanning folder…") }
            state.browseLoading -> Div(attrs = { classes("status-pad") }) { Text("Loading…") }
            state.browseChildren.isEmpty() -> Div(attrs = { classes("empty-folder") }) { Text("This folder is empty.") }
            else -> Div(attrs = { classes("curated-browse-list") }) {
                state.browseChildren.forEach { child -> CuratedBrowseRow(state, spec, child, peek) }
            }
        }
        if (state.browseError != null) {
            Div(attrs = { classes("status-pad", "error") }) { Text(state.browseError ?: "") }
        }
        Div(attrs = { classes("modal-actions") }) {
            Button(attrs = { classes("btn"); onClick { state.exitBrowsing() } }) { Text("◂ Back to inputs") }
            if (spec.format == CuratedInputFormat.FILE_SET) {
                val count = state.checkedFiles.size
                Button(attrs = {
                    classes("btn", "primary")
                    if (count == 0) attr("disabled", "true")
                    onClick { addCheckedFiles(state, spec) }
                }) { Text("Add checked ($count)") }
            }
        }
    }
}

@Composable
private fun CuratedBrowseRow(
    state: CuratedRunUiState,
    spec: CuratedInputSpec,
    file: FileDto,
    peek: PreviewTooltipStore,
) {
    val isFolder = file.kind == FileKind.FOLDER
    // Dim files that don't satisfy the input's declared file-type constraint — stippling
    // out the CSVs-among-everything-else the reviewer feedback asked for. Folders are
    // NEVER dimmed: a folder is always a navigation target, and for a file-set it is also
    // a legitimate pick in its own right (D21's folder shape) — dimming it would say
    // "this doesn't match" about something that isn't a file at all. Dimmed files stay
    // fully selectable below (checkbox / Select button, unchanged) — this is décor, not a
    // gate; see matchesFileTypeOrUnconstrained's kdoc for why the no-guidance case dims
    // nothing.
    val dimmed = !isFolder && !matchesFileTypeOrUnconstrained(spec, file.displayName)
    Div(attrs = { classes("curated-browse-row"); if (isFolder) classes("folder"); if (dimmed) classes("dimmed") }) {
        if (spec.format == CuratedInputFormat.FILE_SET && !isFolder) {
            CheckboxInput(checked = state.checkedFiles.containsKey(file.id), attrs = {
                onChange { ev ->
                    if (ev.value) state.checkedFiles[file.id] = file else state.checkedFiles.remove(file.id)
                }
            })
        }
        Span(attrs = {
            classes("curated-browse-name")
            if (isFolder) {
                classes("clickable")
                onClick {
                    if (state.source is PickerSource.Shared) state.browseTrail = state.browseTrail + file
                    else state.browsePath = file.logicalPath
                }
            }
        }) {
            Icon(if (isFolder) IconName.FOLDER else IconName.FILE)
            Text(" ${file.displayName}")
        }
        if (!isFolder) {
            // HOVER, not click. A docked panel beside a centred dialog never sat
            // right — two preview surfaces, an off-centre dialog and a reserved
            // gutter — so the picker peeks at the head of the file instead. The
            // trade is explicit: no scrolling and no delimiter/header controls.
            // The file view keeps the full pane.
            //
            // aria-label rather than data-tip: data-tip would raise the generic
            // text tooltip on top of this one.
            Button(attrs = {
                classes("btn", "icon")
                if (peek.target?.id == file.id) classes("active")
                attr("aria-label", "Preview ${file.displayName}")
                onMouseEnter { event ->
                    val rect = event.target.unsafeCast<org.w3c.dom.HTMLElement>().getBoundingClientRect()
                    peek.show(file, TooltipAnchor(rect.left, rect.top, rect.height))
                }
                onMouseLeave { peek.hide() }
                // Keyboard parity: hover is unreachable from the keyboard, so the
                // same peek opens on focus.
                onFocusIn { event ->
                    val rect = event.target.unsafeCast<org.w3c.dom.HTMLElement>().getBoundingClientRect()
                    peek.show(file, TooltipAnchor(rect.left, rect.top, rect.height))
                }
                onFocusOut { peek.hide() }
                // A click would otherwise submit the enclosing form-ish row.
                onClick { event -> event.stopPropagation() }
            }) { Icon(IconName.SEARCH) }
            Span(attrs = { classes("curated-browse-size") }) { Text(formatPickedSize(file.sizeBytes ?: 0L)) }
        }
        // FILE_PATH: a single click both picks the file and returns to the overview — no
        // separate "Add" step makes sense for exactly-one-file. Folders are never
        // selectable here (clicking one only navigates), so a folder pick is structurally
        // impossible for this format, not merely validated against.
        if (!isFolder && spec.format == CuratedInputFormat.FILE_PATH) {
            Button(attrs = { classes("btn"); onClick { pickSingleFile(state, spec, file) } }) { Text("Select") }
        }
        // FILE_SET: a folder can be picked directly from the listing, without navigating
        // into it first — D21's folder shape. Walked immediately via [walkFolder]; the
        // result is reviewed (count/size, deselect) before it's added.
        if (isFolder && spec.format == CuratedInputFormat.FILE_SET) {
            Button(attrs = {
                classes("btn")
                attr("data-tip", "Pick every file in \"${file.displayName}\", including subfolders")
                if (state.scanning) attr("disabled", "true")
                onClick { startFolderScan(state, spec, file) }
            }) { Text("Use this folder") }
        }
    }
}

/** The "N files, X size — deselect before adding" review after a folder walk. */
@Composable
private fun FolderScanReviewView(state: CuratedRunUiState, spec: CuratedInputSpec, review: FolderScanReview) {
    val kept = review.picks.filter { it.fileId !in review.deselected }
    Div(attrs = { classes("curated-scan-review") }) {
        Div(attrs = { classes("curated-scan-summary") }) {
            Text("${kept.size} file${if (kept.size == 1) "" else "s"} · ${formatPickedSize(totalPickedBytes(kept))}")
        }
        // State the arithmetic when the walk actually dropped anything (reviewer feedback:
        // silently discarding files the scientist believes they picked is worse than not
        // filtering at all). Nothing is shown when totalFiles == picks.size — either the
        // input carries no file-type guidance (dropped nothing by design) or every file in
        // the folder happened to match; either way there is nothing to report.
        val skipped = review.totalFiles - review.picks.size
        if (skipped > 0) {
            Div(attrs = { classes("curated-scan-arithmetic") }) {
                Text("${review.picks.size} of ${review.totalFiles} files matched — $skipped skipped")
            }
        }
        Div(attrs = { classes("curated-scan-list") }) {
            review.picks.forEach { pick ->
                val checked = pick.fileId !in review.deselected
                Label(attrs = { classes("curated-scan-row") }) {
                    CheckboxInput(checked = checked, attrs = {
                        onChange { ev ->
                            if (ev.value) review.deselected.remove(pick.fileId) else review.deselected.add(pick.fileId)
                        }
                    })
                    Span(attrs = { classes("curated-pick-rel") }) { Text(pick.rel) }
                    Span(attrs = { classes("curated-pick-size") }) { Text(formatPickedSize(pick.sizeBytes ?: 0L)) }
                }
            }
        }
        if (state.browseError != null) {
            Div(attrs = { classes("status-pad", "error") }) { Text(state.browseError ?: "") }
        }
        Div(attrs = { classes("modal-actions") }) {
            Button(attrs = { classes("btn"); onClick { state.scanReview = null } }) { Text("◂ Back") }
            Button(attrs = {
                classes("btn", "primary")
                if (kept.isEmpty()) attr("disabled", "true")
                onClick { addScanReview(state, spec, kept) }
            }) { Text("Add ${kept.size} file${if (kept.size == 1) "" else "s"}") }
        }
    }
}

private fun pickSingleFile(state: CuratedRunUiState, spec: CuratedInputSpec, file: FileDto) {
    state.picksByInput[spec.name] = listOf(
        CuratedPick(file.id, scatteredPickRel(file), file.displayName, file.sizeBytes),
    )
    state.exitBrowsing()
}

private fun addCheckedFiles(state: CuratedRunUiState, spec: CuratedInputSpec) {
    val additions = state.checkedFiles.values.map { file ->
        CuratedPick(file.id, scatteredPickRel(file), file.displayName, file.sizeBytes)
    }
    mergeInto(state, spec, additions)?.let { state.exitBrowsing() }
}

private fun addScanReview(state: CuratedRunUiState, spec: CuratedInputSpec, kept: List<CuratedPick>) {
    if (mergeInto(state, spec, kept) != null) {
        state.scanReview = null
        state.exitBrowsing()
    }
}

/**
 * Merge [additions] into [spec]'s picks. On a cross-directory basename collision
 * (D21/TARGET_COLLISION — surfaced early, before an hour-long run), the whole batch
 * is rejected and named in [CuratedRunUiState.browseError] rather than landing
 * partially; returns null so the caller knows NOT to leave the browse/review screen.
 */
private fun mergeInto(state: CuratedRunUiState, spec: CuratedInputSpec, additions: List<CuratedPick>): Unit? {
    val existing = state.picksByInput[spec.name].orEmpty()
    val result = mergePicks(existing, additions)
    if (result.collidedRels.isNotEmpty()) {
        state.browseError = "These files share a name with one already picked — rename or remove one: " +
            result.collidedRels.distinct().joinToString(", ")
        return null
    }
    state.picksByInput[spec.name] = result.merged
    return Unit
}

private fun startFolderScan(state: CuratedRunUiState, spec: CuratedInputSpec, folder: FileDto) {
    if (state.scanning) return
    state.scanning = true
    state.browseError = null
    MainScope().launch {
        try {
            val result = if (state.source is PickerSource.Shared) {
                walkSharedFolder(folder, spec) { id -> FilesApi.listSubtreeById(id) }
            } else {
                walkFolder(folder.logicalPath, spec) { path -> FilesApi.listFiles(path) }
            }
            when {
                result.totalFiles == 0 ->
                    state.browseError = "\"${folder.displayName}\" contains no files."
                // The folder had files, but none of them matched — worth its own message
                // rather than reusing "contains no files" (which would be false: it does,
                // just none the input's declared type accepts).
                result.picks.isEmpty() ->
                    state.browseError = "\"${folder.displayName}\" has ${result.totalFiles} file" +
                        "${if (result.totalFiles == 1) "" else "s"}, but none matched the expected " +
                        describeFileTypeGuidance(spec) + "."
                else -> state.scanReview = FolderScanReview(folder.logicalPath, result.picks, result.totalFiles)
            }
        } catch (e: Throwable) {
            state.browseError = e.userFacingMessage()
        } finally {
            state.scanning = false
        }
    }
}
